import { useState } from "react";

import { useAddHospital } from "../../../hooks/mutations/admin/hospitals";
import { useTenants } from "../../../hooks/queries/admin/hospitals";
import { useCountryOptions } from "../../../hooks/queries/admin/selectValues";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  AdminSelectField,
  CheckboxField,
  FieldError,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
} from "../shared";
import { DEFAULT_TIMEZONE, MAX_TEXT_LENGTH, TIMEZONES } from "./hospitalFields";

interface IAddHospitalModalProps {
  /** Lets the list surface the new hospital straight away, as Retool did. */
  onCreated?: (code: string) => void;
  onClose: () => void;
}

export function AddHospitalModal({
  onCreated,
  onClose,
}: IAddHospitalModalProps) {
  const { showToast } = useToast();
  const { countries, isLoading: countriesLoading } = useCountryOptions();
  const { tenants, isLoading: tenantsLoading } = useTenants();
  const { addHospital, isAddingHospital } = useAddHospital();

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [countryId, setCountryId] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [publish, setPublish] = useState(true);
  // An empty create form is invalid by definition — a field only complains
  // once it has been touched (same as AddWardModal).
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const touch = (field: string) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const errors = {
    name: name.trim() ? undefined : "Name is required.",
    code: code.trim() ? undefined : "Code is required.",
    countryId: countryId ? undefined : "Select a country.",
    tenantId: tenantId ? undefined : "Select a tenant.",
  };
  const hasErrors = Object.values(errors).some(Boolean);
  const shownError = (field: keyof typeof errors) =>
    touched[field] ? errors[field] : undefined;

  const handleAdd = async () => {
    if (hasErrors) return;

    try {
      await addHospital({
        name: name.trim(),
        code: code.trim(),
        timezone,
        countryId: Number(countryId),
        tenantId,
        // Retool hard-coded order 1 on create; the API defaults it to 0 and
        // nothing on this page reorders, so the Retool value is kept.
        order: 1,
        publish,
      });
      showToast("The hospital was created successfully.", "success");
      onCreated?.(code.trim());
      onClose();
    } catch (error) {
      showToast(
        errorToastMessage(error, "There was an error creating this hospital."),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Create hospital</ModalTitle>

      <ModalForm>
        <FilterGroup>
          <FilterLabel htmlFor="new-hospital-name">Name</FilterLabel>
          <FilterInput
            id="new-hospital-name"
            type="text"
            maxLength={MAX_TEXT_LENGTH}
            value={name}
            onBlur={() => touch("name")}
            onChange={(e) => {
              touch("name");
              setName(e.target.value);
            }}
          />
          {shownError("name") && <FieldError>{shownError("name")}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="new-hospital-code">Code</FilterLabel>
          <FilterInput
            id="new-hospital-code"
            type="text"
            maxLength={MAX_TEXT_LENGTH}
            value={code}
            onBlur={() => touch("code")}
            onChange={(e) => {
              touch("code");
              setCode(e.target.value);
            }}
          />
          {shownError("code") && <FieldError>{shownError("code")}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="new-hospital-timezone">Timezone</FilterLabel>
          <AdminSelectField
            id="new-hospital-timezone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
          >
            {TIMEZONES.map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="new-hospital-country">Country</FilterLabel>
          <AdminSelectField
            id="new-hospital-country"
            value={countryId}
            disabled={countriesLoading}
            onBlur={() => touch("countryId")}
            onChange={(e) => {
              touch("countryId");
              setCountryId(e.target.value);
            }}
          >
            <option value="">
              {countriesLoading ? "Loading..." : "Select an option"}
            </option>
            {countries.map((country) => (
              <option key={country.id} value={country.id}>
                {country.name}
              </option>
            ))}
          </AdminSelectField>
          {shownError("countryId") && (
            <FieldError>{shownError("countryId")}</FieldError>
          )}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="new-hospital-tenant">Tenant</FilterLabel>
          <AdminSelectField
            id="new-hospital-tenant"
            value={tenantId}
            disabled={tenantsLoading}
            onBlur={() => touch("tenantId")}
            onChange={(e) => {
              touch("tenantId");
              setTenantId(e.target.value);
            }}
          >
            <option value="">
              {tenantsLoading ? "Loading..." : "Select an option"}
            </option>
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </AdminSelectField>
          {shownError("tenantId") && (
            <FieldError>{shownError("tenantId")}</FieldError>
          )}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Visibility</FilterLabel>
          <CheckboxField>
            <input
              type="checkbox"
              checked={publish}
              onChange={(e) => setPublish(e.target.checked)}
            />
            Published
          </CheckboxField>
        </FilterGroup>

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isAddingHospital}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isAddingHospital || hasErrors}
            onClick={handleAdd}
          >
            {isAddingHospital ? "Creating..." : "Create"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
